import { IEleve } from 'app/shared/model/eleve.model';
import { IMatiereEcole } from 'app/shared/model/matiere.model';
import { IDispenseCours, IDispenseCoursGroup } from 'app/shared/model/dispense-cours.model';

export interface DispenseEleveOption {
  value: string;
  label: string;
}

export const getDispensesCoursColumnKeys = (): string[] => ['classeNom', 'matiereNom', 'motif', 'effectif', 'actions'];

export const buildDispenseEleveOptions = (eleves: ReadonlyArray<IEleve> = []): DispenseEleveOption[] =>
  eleves
    .filter(eleve => !!eleve.inscriptionId)
    .map(eleve => ({
      value: eleve.inscriptionId,
      label: [eleve.firstName, eleve.lastName].filter(Boolean).join(' ') + (eleve.matricule ? ` - ${eleve.matricule}` : ''),
    }));

export interface DispenseInscriptionOptionParams {
  classeId?: string;
  matiereId?: string;
  sousMatiereId?: string;
  search?: string;
}

export const groupDispensesCours = (dispenses: ReadonlyArray<IDispenseCours> = []): IDispenseCoursGroup[] => {
  const groupsByKey = new Map<string, IDispenseCoursGroup>();

  dispenses.forEach(dispense => {
    const sousMatiereId = normalizeOptional(dispense.sousMatiereId);
    const classeId = dispense.classeId || '';
    const matiereId = dispense.matiereId || '';
    const key = `${classeId}|${matiereId}|${sousMatiereId || ''}`;
    const group =
      groupsByKey.get(key) ||
      ({
        key,
        classeId,
        classeNom: dispense.classeNom,
        matiereId,
        matiereNom: dispense.matiereNom,
        sousMatiereId,
        sousMatiereNom: dispense.sousMatiereNom,
        motif: undefined,
        motifsMultiples: false,
        effectif: 0,
        dispenses: [],
      } as IDispenseCoursGroup);

    group.dispenses.push(dispense);
    group.effectif = group.dispenses.length;
    groupsByKey.set(key, group);
  });

  return Array.from(groupsByKey.values())
    .map(group => {
      const motifs = Array.from(new Set(group.dispenses.map(dispense => normalizeOptional(dispense.motif))));
      const sortedDispenses = [...group.dispenses].sort(compareDispensesByStudent);
      return {
        ...group,
        dispenses: sortedDispenses,
        effectif: sortedDispenses.length,
        motif: motifs.length === 1 ? motifs[0] : undefined,
        motifsMultiples: motifs.length > 1,
      };
    })
    .sort(compareGroups);
};

export const buildDispenseInscriptionOptions = (
  eleves: ReadonlyArray<IEleve> = [],
  dispenses: ReadonlyArray<IDispenseCours> = [],
  params: DispenseInscriptionOptionParams = {}
): DispenseEleveOption[] => {
  const search = normalizeForSearch(params.search);
  return eleves
    .filter(eleve => !!eleve.inscriptionId)
    .filter(eleve => !params.classeId || !eleve.classe?.id || eleve.classe?.id === params.classeId)
    .filter(
      eleve => !search || normalizeForSearch([eleve.firstName, eleve.lastName, eleve.matricule].filter(Boolean).join(' ')).includes(search)
    )
    .filter(eleve => !hasDispenseConflictForTarget(dispenses, eleve.inscriptionId, params.matiereId, params.sousMatiereId))
    .sort((left, right) => formatEleveName(left).localeCompare(formatEleveName(right), undefined, { sensitivity: 'base' }))
    .map(eleve => ({
      value: eleve.inscriptionId,
      label: formatEleveName(eleve),
    }));
};

export const getAvailableMatieresForDispense = (
  matieres: ReadonlyArray<IMatiereEcole> = [],
  dispenses: ReadonlyArray<IDispenseCours> = [],
  inscriptionId?: string | null
): IMatiereEcole[] => {
  if (!inscriptionId) return [...matieres];
  const dispensedMatiereIds = new Set(
    dispenses.filter(dispense => dispense.inscriptionId === inscriptionId && !dispense.sousMatiereId).map(dispense => dispense.matiereId)
  );
  return matieres.filter(matiere => !dispensedMatiereIds.has(matiere.id));
};

export const formatDispenseMatiereLabel = (dispense: Pick<IDispenseCours, 'matiereNom' | 'sousMatiereNom'>): string => {
  if (dispense.sousMatiereNom) {
    return `${dispense.matiereNom || '-'} / ${dispense.sousMatiereNom}`;
  }
  return dispense.matiereNom || '-';
};

const normalizeOptional = (value?: string | null): string | undefined => {
  if (!value || !value.trim()) {
    return undefined;
  }
  return value.trim();
};

const normalizeForSearch = (value?: string | null): string => normalizeOptional(value)?.toLocaleLowerCase() || '';

const formatEleveName = (eleve: Pick<IEleve, 'firstName' | 'lastName' | 'matricule'>): string =>
  [eleve.firstName, eleve.lastName].filter(Boolean).join(' ') + (eleve.matricule ? ` - ${eleve.matricule}` : '');

const hasDispenseConflictForTarget = (
  dispenses: ReadonlyArray<IDispenseCours>,
  inscriptionId?: string,
  matiereId?: string,
  sousMatiereId?: string
): boolean => {
  if (!inscriptionId || !matiereId) {
    return false;
  }
  return dispenses.some(dispense => {
    if (dispense.inscriptionId !== inscriptionId || dispense.matiereId !== matiereId) {
      return false;
    }
    const existingSousMatiereId = normalizeOptional(dispense.sousMatiereId);
    const targetSousMatiereId = normalizeOptional(sousMatiereId);
    return !targetSousMatiereId || !existingSousMatiereId || existingSousMatiereId === targetSousMatiereId;
  });
};

const compareDispensesByStudent = (left: IDispenseCours, right: IDispenseCours): number =>
  [left.eleveNom, left.elevePrenom, left.matricule, left.id]
    .filter(Boolean)
    .join(' ')
    .localeCompare([right.eleveNom, right.elevePrenom, right.matricule, right.id].filter(Boolean).join(' '), undefined, {
      sensitivity: 'base',
    });

const compareGroups = (left: IDispenseCoursGroup, right: IDispenseCoursGroup): number =>
  [left.classeNom, left.matiereNom, left.sousMatiereNom, left.key]
    .filter(Boolean)
    .join(' ')
    .localeCompare([right.classeNom, right.matiereNom, right.sousMatiereNom, right.key].filter(Boolean).join(' '), undefined, {
      sensitivity: 'base',
    });
